import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import colors from 'hsl-shared-components/lib/Theme/colors';
import { Trans } from 'react-i18next';
import { formatPrice } from '../../../price';
import {
  getRelativeHeight,
  getRelativeWidth,
} from '../../../components/CardReader/CardReader';

const ExtraTicketConfirmation = styled.div`
  position: absolute;
  top: ${getRelativeHeight(76)};
  left: ${getRelativeWidth(13)};
  right: ${getRelativeWidth(13)};
  bottom: ${getRelativeHeight(43)};
  color: ${colors.primary.hslGreyDark};
`;

const ValueTicketTitle = styled.div`
  position: absolute;
  top: -33.5%;
  left: 1.333333%;
  font-weight: 500;
  color: ${colors.primary.hslWhite};
`;

const ExtraTickets = styled.div`
  position: absolute;
  font-size: 1.4em;
  top: ${getRelativeHeight(-28)};
  left: ${getRelativeWidth(36)};
`;

const ExtraTicket = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${getRelativeHeight(15)} 0 ${getRelativeHeight(10)};

  & + div {
    border-top: 1px solid ${colors.primary.hslGreyXLight};
  }

  & > div {
    font-size: 1.4em;
    font-weight: 700;

    &:first-child {
      font-size: 0.95em;
      text-align: right;
      flex-grow: 1;
      align-self: center;
    }
  }
`;

const ExtraTicketMultiplier = styled.span`
  font-weight: 400;
`;

const ValueTicketValidity = styled.div`
  position: absolute;
  font-size: 0.85em;
  top: ${getRelativeHeight(54)};
  left: ${getRelativeWidth(5)};
`;

const TotalPrice = styled.div`
  position: absolute;
  font-size: 0.85em;
  font-weight: 700;
  top: ${getRelativeHeight(194)};
  right: ${getRelativeWidth(214)};
`;

const RemainingFundsTitle = styled.div`
  color: ${colors.primary.hslGreyDark};
  position: absolute;
  font-size: 0.85em;
  font-weight: 300;
  top: ${getRelativeHeight(264)};
  left: ${getRelativeWidth(5)};
`;

const RemainingFunds = styled.div`
  position: absolute;
  font-size: 0.85em;
  font-weight: 700;
  top: ${getRelativeHeight(264)};
  right: ${getRelativeWidth(214)};
`;

const SeasonalTicketTitle = styled.div`
  position: absolute;
  top: -33.5%;
  right: 24.333333%;
  font-weight: 500;
  color: ${colors.primary.hslWhite};
`;

const SeasonalTicketZone = styled.div`
  position: absolute;
  font-size: 1.4em;
  font-weight: 700;
  top: ${getRelativeHeight(-20)};
  left: ${getRelativeWidth(244)};
`;

const SeasonalTicketValidity = styled.div`
  position: absolute;
  font-size: 0.85em;
  top: ${getRelativeHeight(54)};
  left: ${getRelativeWidth(214)};
`;

const ExtraTicketConfirmationScreen = ({
  valueTicket,
  seasonalTicket,
  remainingFunds,
}) => {
  const valueTicketPrice = (valueTicket && (valueTicket.price * valueTicket.passengers)) || 0; // eslint-disable-line
  const totalPrice = formatPrice(valueTicketPrice);
  return (
    <ExtraTicketConfirmation>
      <ValueTicketTitle>
        <Trans i18nKey="Generic.singleTicket" />
      </ValueTicketTitle>
      <ExtraTickets>
        {valueTicket && (
          <ExtraTicket>
            <div>
              {valueTicket.passengers}{' '}
              <ExtraTicketMultiplier>x</ExtraTicketMultiplier>{' '}
              {valueTicket.zones}
            </div>
          </ExtraTicket>
        )}
      </ExtraTickets>
      <ValueTicketValidity>
        <Trans i18nKey="Generic.validityAbbr" />{' '}
        <strong>{valueTicket.validityEndsAtString}</strong>
      </ValueTicketValidity>
      <TotalPrice>
        <Trans i18nKey="Generic.charged" /> {totalPrice}
      </TotalPrice>

      <RemainingFundsTitle>
        <Trans i18nKey="Generic.valueRemaining" />
      </RemainingFundsTitle>
      <RemainingFunds>{formatPrice(remainingFunds)}</RemainingFunds>

      <SeasonalTicketTitle>
        <Trans i18nKey="Generic.seasonalTicket" />
      </SeasonalTicketTitle>
      <SeasonalTicketZone>{seasonalTicket.zones}</SeasonalTicketZone>
      <SeasonalTicketValidity>
        <Trans i18nKey="Generic.validityAbbr" />{' '}
        <strong>{seasonalTicket.validityEndsAtString}</strong>
      </SeasonalTicketValidity>
    </ExtraTicketConfirmation>
  );
};

ExtraTicketConfirmationScreen.propTypes = {
  seasonalTicket: PropTypes.shape({
    zones: PropTypes.string.isRequired,
    validityEndsAtString: PropTypes.string.isRequired,
  }).isRequired,
  valueTicket: PropTypes.shape({
    zones: PropTypes.string.isRequired,
    passengers: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityEndsAtString: PropTypes.string.isRequired,
  }),
  remainingFunds: PropTypes.string.isRequired,
};

ExtraTicketConfirmationScreen.defaultProps = {
  valueTicket: null,
};

export default ExtraTicketConfirmationScreen;
